import { WORLD } from "@clubap/shared";
import { Container, Graphics } from "pixi.js";

const FLOOR_Y = 250;

/**
 * Sala del club TAWS: investigación en desarrollo web y móvil, dentro de FIEC.
 *
 * Deliberadamente más cálida y desordenada que `fiec`, que es un laboratorio
 * formal con filas de escritorios. Ésta es una sala de proyecto: pizarra con
 * bocetos, mesas de trabajo, un sofá. Si las dos salas se vieran igual, cruzar la
 * puerta no se sentiría como ir a otro lado.
 */
export function taws(): Container {
  const scene = new Container();

  const walls = new Graphics();
  walls.rect(0, 0, WORLD.width, FLOOR_Y).fill(0xf0e8dc);
  walls.rect(0, FLOOR_Y - 14, WORLD.width, 14).fill(0xd6c9b6);
  scene.addChild(walls);

  scene.addChild(whiteboard(250, 40));
  scene.addChild(banner(700, 34));
  scene.addChild(window_(860, 60));

  const floor = new Graphics();
  floor.rect(0, FLOOR_Y, WORLD.width, WORLD.height - FLOOR_Y).fill(0xbfa98c);
  // Duelas, sugeridas con líneas que convergen levemente.
  for (let i = 0; i <= 10; i++) {
    const x = (WORLD.width / 10) * i;
    floor
      .moveTo(x, FLOOR_Y)
      .lineTo(x + (x - WORLD.width / 2) * 0.3, WORLD.height)
      .stroke({ width: 1, color: 0xa8916f, alpha: 0.55 });
  }
  // Alfombra: marca la zona donde de verdad se camina.
  floor.ellipse(470, 400, 250, 92).fill({ color: 0xa8b6a0, alpha: 0.5 });
  scene.addChild(floor);

  scene.addChild(couch(140, FLOOR_Y + 78));
  scene.addChild(deskWithLaptop(690, FLOOR_Y + 40));
  scene.addChild(deskWithLaptop(830, FLOOR_Y + 72));
  scene.addChild(plant(60, FLOOR_Y + 120));

  return scene;
}

/** Pizarra con wireframes: lo que dibuja un club de desarrollo web. */
function whiteboard(x: number, y: number): Container {
  const board = new Container();
  board.x = x;
  board.y = y;

  const g = new Graphics();
  g.rect(0, 0, 300, 160).fill(0xfbfbf8).stroke({ width: 6, color: 0x9aa7b0 });

  // Bocetos de pantallas.
  g.rect(20, 20, 70, 110).stroke({ width: 3, color: 0x4a6fa5 });
  g.rect(26, 28, 58, 14).fill(0x9fc4e8);
  g.rect(26, 50, 58, 8).fill(0xd6e4f2);
  g.rect(26, 64, 40, 8).fill(0xd6e4f2);
  g.rect(26, 100, 58, 22).fill(0xa8d8b0);

  g.rect(110, 20, 110, 70).stroke({ width: 3, color: 0x4a6fa5 });
  g.rect(116, 26, 98, 12).fill(0x9fc4e8);
  g.circle(130, 62, 10).fill(0xd6e4f2);
  g.rect(148, 54, 60, 8).fill(0xd6e4f2);
  g.rect(148, 68, 42, 8).fill(0xd6e4f2);

  // Flechas de flujo entre pantallas.
  g.moveTo(96, 70).lineTo(106, 70).stroke({ width: 3, color: 0xd98032 });
  g.moveTo(106, 70).lineTo(101, 66).moveTo(106, 70).lineTo(101, 74).stroke({
    width: 3,
    color: 0xd98032,
  });

  g.moveTo(120, 108).lineTo(280, 108).stroke({ width: 3, color: 0x8a8a8a });
  g.moveTo(120, 124).lineTo(240, 124).stroke({ width: 3, color: 0x8a8a8a });
  g.moveTo(120, 140).lineTo(265, 140).stroke({ width: 3, color: 0x8a8a8a });

  board.addChild(g);
  return board;
}

function banner(x: number, y: number): Container {
  const sign = new Container();
  sign.x = x;
  sign.y = y;

  const g = new Graphics();
  g.rect(0, 0, 130, 54).fill(0x1f5c8c).stroke({ width: 4, color: 0x14405f });
  // Letras sugeridas con bloques: texto real iría en el fondo definitivo.
  const bars = [8, 38, 68, 98];
  for (const bx of bars) g.rect(bx, 18, 22, 20).fill(0xffd75e);
  sign.addChild(g);

  return sign;
}

function window_(x: number, y: number): Container {
  const frame = new Container();
  frame.x = x;
  frame.y = y;

  const g = new Graphics();
  g.rect(0, 0, 120, 130).fill(0x9fd3ea).stroke({ width: 5, color: 0xf4f7fa });
  g.moveTo(60, 0).lineTo(60, 130).stroke({ width: 3, color: 0xf4f7fa });
  g.moveTo(0, 65).lineTo(120, 65).stroke({ width: 3, color: 0xf4f7fa });
  frame.addChild(g);

  return frame;
}

function couch(x: number, y: number): Container {
  const seat = new Container();
  seat.x = x;
  seat.y = y;

  const g = new Graphics();
  g.ellipse(0, 0, 96, 18).fill({ color: 0x000000, alpha: 0.1 });
  g.roundRect(-92, -74, 184, 62, 12).fill(0x8c5f7a);
  g.roundRect(-92, -30, 184, 32, 10).fill(0x9c6d88);
  g.roundRect(-96, -46, 16, 46, 8).fill(0x7d5069);
  g.roundRect(80, -46, 16, 46, 8).fill(0x7d5069);
  // Cojines.
  g.roundRect(-70, -66, 52, 34, 8).fill({ color: 0xffd75e, alpha: 0.85 });
  g.roundRect(18, -66, 52, 34, 8).fill({ color: 0xa8d8b0, alpha: 0.85 });
  seat.addChild(g);

  return seat;
}

function deskWithLaptop(x: number, y: number): Container {
  const unit = new Container();
  unit.x = x;
  unit.y = y;

  const g = new Graphics();
  g.ellipse(0, 0, 58, 14).fill({ color: 0x000000, alpha: 0.1 });
  g.rect(-54, -34, 108, 9).fill(0xd8c9a8);
  g.rect(-48, -25, 6, 25).fill(0x9aa7b0);
  g.rect(42, -25, 6, 25).fill(0x9aa7b0);

  // Portátil abierto: base y pantalla con líneas de código.
  g.poly([-30, -34, 30, -34, 24, -40, -24, -40]).fill(0xb8c2cc);
  g.rect(-24, -76, 48, 36).fill(0x2b3a45);
  g.rect(-20, -72, 40, 28).fill(0x38505f);
  for (let i = 0; i < 4; i++) {
    g.rect(-17, -68 + i * 6, 12 + ((i * 7) % 20), 3).fill(0x7fd4a8);
  }
  unit.addChild(g);

  return unit;
}

function plant(x: number, y: number): Container {
  const pot = new Container();
  pot.x = x;
  pot.y = y;

  const g = new Graphics();
  g.ellipse(0, 0, 26, 8).fill({ color: 0x000000, alpha: 0.1 });
  g.poly([-20, 0, 20, 0, 15, -34, -15, -34]).fill(0xc4763f);
  for (const [dx, dy] of [
    [-26, -78],
    [0, -96],
    [26, -76],
    [-14, -62],
    [16, -60],
  ] as Array<[number, number]>) {
    g.moveTo(0, -34).quadraticCurveTo(dx * 0.5, (dy - 34) * 0.6, dx, dy);
    g.stroke({ width: 6, color: 0x3f8f5a });
  }
  pot.addChild(g);

  return pot;
}
